import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Search, PackagePlus, ShoppingBag, Camera, X, Boxes } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const P_STATUS = {
  announced: ["Annoncé", "bg-secondary text-primary"],
  in_transit: ["En route", "bg-blue-100 text-blue-700"],
  partial: ["Partiel", "bg-amber-100 text-amber-700"],
  received: ["Reçu", "bg-emerald-600 text-white"],
  unannounced: ["Non annoncé", "bg-red-100 text-red-700"],
  consolidated: ["Consolidé", "bg-accent/20 text-accent"],
};
const emptyF = { purchase_id: "", client_code: "", cartons: 1, weight_kg: 0, length_cm: 0, width_cm: 0, height_cm: 0, condition: "good", damaged: false, notes: "", shop_name: "", tracking_number: "" };

export const WarehouseShopShip = () => {
  const [purchases, setPurchases] = useState([]);
  const [lookup, setLookup] = useState("");
  const [found, setFound] = useState(null);
  const [f, setF] = useState(emptyF);
  const [photos, setPhotos] = useState([]);
  const load = () => api.get("/warehouse/shopship/purchases").then(({ data }) => setPurchases(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const doLookup = async () => {
    if (!lookup.trim()) return;
    try {
      const { data } = await api.get(`/warehouse/shopship/lookup?code=${encodeURIComponent(lookup.trim())}`);
      setFound(data);
      setF({ ...emptyF, client_code: data.customer.client_code });
      toast.success(`Client : ${data.customer.name} (${data.customer.client_code})`);
    } catch (e) { setFound(null); toast.error(e.response?.data?.detail || "Introuvable"); }
  };

  const prefill = (p) => {
    setF({ ...emptyF, purchase_id: p.id, client_code: p.client_code, cartons: 1, shop_name: p.shop_name, tracking_number: p.tracking_number || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info(`Réception : ${p.shop_name} · ${p.client_code}`);
  };

  const addPhoto = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - photos.length);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => setPhotos((p) => [...p, reader.result]);
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const receive = async () => {
    if (!f.purchase_id && !f.client_code) { toast.error("Sélectionnez un achat ou saisissez un code client"); return; }
    try {
      const { data } = await api.post("/warehouse/shopship/receive", {
        ...f, cartons: Number(f.cartons) || 1, weight_kg: Number(f.weight_kg) || 0,
        length_cm: Number(f.length_cm) || 0, width_cm: Number(f.width_cm) || 0, height_cm: Number(f.height_cm) || 0,
        photos,
      });
      toast.success(data.purchase_status === "partial" ? "Réception partielle enregistrée" : "Colis réceptionné — client notifié");
      setF(emptyF); setPhotos([]); setFound(null); setLookup(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const expected = purchases.filter((p) => ["announced", "in_transit", "partial"].includes(p.status));
  const selPurchase = purchases.find((p) => p.id === f.purchase_id);

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="border border-border bg-card p-5">
        <h2 className="font-heading font-bold text-lg flex items-center gap-2"><PackagePlus className="h-5 w-5 text-accent" /> Réception Shop &amp; Ship</h2>
        <div className="mt-4 space-y-3">
          <div><Label>Rechercher un client (code C2G-XXXXX ou tracking)</Label>
            <div className="flex gap-2">
              <Input data-testid="ss-lookup-input" className="rounded-sm" placeholder="C2G-10001" value={lookup}
                onChange={(e) => setLookup(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doLookup()} />
              <Button data-testid="ss-lookup-btn" variant="outline" className="rounded-sm shrink-0 gap-1" onClick={doLookup}><Search className="h-4 w-4" /> Rechercher</Button>
            </div>
          </div>
          {found && (
            <div className="border border-accent/40 bg-accent/5 p-3 text-sm" data-testid="ss-lookup-result">
              <div className="font-semibold">{found.customer.name} · {found.customer.client_code}</div>
              {found.purchases.length > 0 ? (
                <div className="mt-1.5 space-y-1">
                  {found.purchases.map((p) => (
                    <button key={p.id} data-testid={`ss-pick-${p.id}`} onClick={() => setF({ ...f, purchase_id: p.id, shop_name: p.shop_name })}
                      className={`block w-full text-left border p-1.5 text-xs ${f.purchase_id === p.id ? "border-accent bg-accent/10" : "border-border"}`}>
                      {p.shop_name} · {p.packages_expected} colis attendu(s) {p.tracking_number && `· ${p.tracking_number}`}
                    </button>
                  ))}
                </div>
              ) : <p className="text-xs text-muted-foreground mt-1">Aucun achat déclaré — la réception créera un COLIS NON ANNONCÉ.</p>}
            </div>
          )}
          {selPurchase && <div className="text-xs text-muted-foreground">Achat sélectionné : <strong>{selPurchase.shop_name}</strong> — {(selPurchase.packages || []).reduce((a, x) => a + x.cartons, 0)}/{selPurchase.packages_expected} déjà reçus</div>}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Cartons reçus</Label><Input data-testid="ss-recv-cartons" type="number" className="rounded-sm" value={f.cartons} onChange={(e) => setF({ ...f, cartons: e.target.value })} /></div>
            <div><Label>Poids (kg)</Label><Input data-testid="ss-recv-weight" type="number" className="rounded-sm" value={f.weight_kg} onChange={(e) => setF({ ...f, weight_kg: e.target.value })} /></div>
          </div>
          <div><Label>Dimensions (L × l × H cm) — CBM auto</Label>
            <div className="grid grid-cols-3 gap-2">
              <Input type="number" placeholder="L" className="rounded-sm" value={f.length_cm} onChange={(e) => setF({ ...f, length_cm: e.target.value })} />
              <Input type="number" placeholder="l" className="rounded-sm" value={f.width_cm} onChange={(e) => setF({ ...f, width_cm: e.target.value })} />
              <Input type="number" placeholder="H" className="rounded-sm" value={f.height_cm} onChange={(e) => setF({ ...f, height_cm: e.target.value })} />
            </div>
            {f.length_cm > 0 && f.width_cm > 0 && f.height_cm > 0 && (
              <p className="mt-1 text-xs text-muted-foreground">CBM : {((f.length_cm * f.width_cm * f.height_cm) / 1000000).toFixed(3)}</p>
            )}
          </div>
          {!f.purchase_id && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Boutique (si connue)</Label><Input data-testid="ss-recv-shop" className="rounded-sm" value={f.shop_name} onChange={(e) => setF({ ...f, shop_name: e.target.value })} /></div>
              <div><Label>Tracking</Label><Input className="rounded-sm" value={f.tracking_number} onChange={(e) => setF({ ...f, tracking_number: e.target.value })} /></div>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
              <input data-testid="ss-recv-damaged" type="checkbox" className="h-4 w-4 accent-[#FF6600]" checked={f.damaged}
                onChange={(e) => setF({ ...f, damaged: e.target.checked, condition: e.target.checked ? "damaged" : "good" })} /> Colis endommagé
            </label>
            <label className="flex items-center gap-1.5 text-sm font-medium cursor-pointer text-accent">
              <Camera className="h-4 w-4" /> Ajouter des photos ({photos.length}/5)
              <input type="file" accept="image/*" multiple className="hidden" onChange={addPhoto} data-testid="ss-recv-photos" />
            </label>
          </div>
          {photos.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {photos.map((ph, i) => (
                <div key={i} className="relative">
                  <img src={ph} alt="" className="h-14 w-14 object-cover border" />
                  <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full p-0.5"><X className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
          )}
          <div><Label>Notes / état</Label><Textarea data-testid="ss-recv-notes" className="rounded-sm" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
          <Button data-testid="ss-recv-submit" className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" onClick={receive}>Enregistrer la réception</Button>
        </div>
      </div>

      <div className="space-y-5">
        <LotsQueue />
        <div className="border border-border bg-card p-5">
          <h3 className="font-heading font-bold flex items-center gap-2"><ShoppingBag className="h-4 w-4 text-accent" /> Colis attendus ({expected.length})</h3>
          <div className="mt-3 space-y-2">
            {expected.map((p) => {
              const [lbl, cls] = P_STATUS[p.status] || [p.status, ""];
              return (
                <div key={p.id} data-testid={`ss-expected-${p.id}`} className="border border-border p-3 text-sm flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span className="font-semibold">{p.shop_name}</span> · {p.customer_name} ({p.client_code})
                    <div className="text-xs text-muted-foreground">
                      {(p.packages || []).reduce((a, x) => a + x.cartons, 0)}/{p.packages_expected} colis
                      {p.tracking_number && ` · ${p.tracking_number}`}{p.dest_country && ` · → ${p.dest_country}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`rounded-sm ${cls}`}>{lbl}</Badge>
                    <Button size="sm" variant="outline" className="rounded-sm" data-testid={`ss-receive-${p.id}`} onClick={() => prefill(p)}>Réceptionner</Button>
                  </div>
                </div>
              );
            })}
            {expected.length === 0 && <p className="text-sm text-muted-foreground">Aucun colis attendu.</p>}
          </div>
        </div>
        <div className="border border-border bg-card p-5">
          <h3 className="font-heading font-bold text-sm">Historique récent</h3>
          <div className="mt-2 space-y-1.5">
            {purchases.filter((p) => ["received", "unannounced", "consolidated"].includes(p.status)).slice(0, 10).map((p) => {
              const [lbl, cls] = P_STATUS[p.status] || [p.status, ""];
              return (
                <div key={p.id} className="flex items-center justify-between text-sm border border-border p-2">
                  <span>{p.shop_name} · {p.client_code}</span>
                  <Badge className={`rounded-sm ${cls}`}>{lbl}</Badge>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const LOT_TASK = {
  to_consolidate: ["À CONSOLIDER", "bg-purple-100 text-purple-700"],
  to_prepare: ["À PRÉPARER (colis séparés)", "bg-blue-100 text-blue-700"],
  consolidated: ["Consolidation terminée", "bg-emerald-600 text-white"],
  prepared: ["Colis préparés", "bg-emerald-600 text-white"],
};

function LotsQueue() {
  const [lots, setLots] = useState([]);
  const [sel, setSel] = useState(null);
  const empty = { cartons: 1, weight_kg: 0, length_cm: 0, width_cm: 0, height_cm: 0, notes: "" };
  const [f, setF] = useState(empty);
  const [photos, setPhotos] = useState([]);
  const load = () => api.get("/warehouse/shopship/lots").then(({ data }) => setLots(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const addPhoto = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - photos.length);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => setPhotos((p) => [...p, reader.result]);
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const submit = async () => {
    try {
      const { data } = await api.post(`/warehouse/shopship/lots/${sel.id}/consolidate`, {
        cartons: Number(f.cartons) || 1, weight_kg: Number(f.weight_kg) || 0,
        length_cm: Number(f.length_cm) || 0, width_cm: Number(f.width_cm) || 0, height_cm: Number(f.height_cm) || 0,
        photos, notes: f.notes,
      });
      toast.success(data.status === "consolidated" ? "Consolidation terminée — client notifié" : "Colis préparés — client notifié");
      setSel(null); setF(empty); setPhotos([]); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const todo = lots.filter((l) => ["to_consolidate", "to_prepare"].includes(l.status));
  const done = lots.filter((l) => ["consolidated", "prepared"].includes(l.status)).slice(0, 5);

  return (
    <div className="border border-border bg-card p-5" data-testid="wh-lots-queue">
      <h3 className="font-heading font-bold flex items-center gap-2"><Boxes className="h-4 w-4 text-accent" /> Lots à traiter ({todo.length})</h3>
      <div className="mt-3 space-y-2">
        {todo.map((l) => (
          <div key={l.id} data-testid={`wh-lot-${l.id}`} className="border border-border p-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span><span className="font-heading font-bold">{l.code}</span> · {l.customer_name} ({l.client_code})</span>
              <Badge className={`rounded-sm ${(LOT_TASK[l.status] || [])[1]}`}>{(LOT_TASK[l.status] || [])[0]}</Badge>
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {l.purchases.map((p) => p.shop_name).join(", ")} · {l.totals.cartons} colis · {l.totals.weight_kg} kg{l.totals.cbm > 0 && ` · ${l.totals.cbm} CBM`}
            </div>
            <Button size="sm" data-testid={`wh-lot-process-${l.id}`} className="mt-2 rounded-sm bg-accent text-white"
              onClick={() => { setSel(l); setF({ ...empty, cartons: l.totals.cartons || 1, weight_kg: l.totals.weight_kg || 0 }); setPhotos([]); }}>
              {l.status === "to_consolidate" ? "Saisir la consolidation" : "Marquer colis préparés"}
            </Button>
          </div>
        ))}
        {todo.length === 0 && <p className="text-sm text-muted-foreground">Aucun lot en attente de traitement.</p>}
      </div>
      {done.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {done.map((l) => (
            <div key={l.id} className="flex items-center justify-between text-xs border border-border p-2">
              <span>{l.code} · {l.client_code}{l.consolidation && ` · ${l.consolidation.cartons} colis · ${l.consolidation.weight_kg} kg`}</span>
              <Badge className={`rounded-sm ${(LOT_TASK[l.status] || [])[1]}`}>{(LOT_TASK[l.status] || [])[0]}</Badge>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent data-testid="wh-lot-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{sel?.status === "to_consolidate" ? "Résultat de la consolidation" : "Résultat de la préparation séparée"} — {sel?.code}</DialogTitle>
            <DialogDescription>Saisissez le nouveau nombre de colis, poids, dimensions et photos. Le client sera notifié.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nombre de colis</Label><Input data-testid="wh-lot-cartons" type="number" className="rounded-sm" value={f.cartons} onChange={(e) => setF({ ...f, cartons: e.target.value })} /></div>
            <div><Label>Poids total (kg)</Label><Input data-testid="wh-lot-weight" type="number" className="rounded-sm" value={f.weight_kg} onChange={(e) => setF({ ...f, weight_kg: e.target.value })} /></div>
            <div className="col-span-2"><Label>Dimensions (L × l × H cm) — CBM auto</Label>
              <div className="grid grid-cols-3 gap-2">
                <Input type="number" placeholder="L" className="rounded-sm" value={f.length_cm} onChange={(e) => setF({ ...f, length_cm: e.target.value })} />
                <Input type="number" placeholder="l" className="rounded-sm" value={f.width_cm} onChange={(e) => setF({ ...f, width_cm: e.target.value })} />
                <Input type="number" placeholder="H" className="rounded-sm" value={f.height_cm} onChange={(e) => setF({ ...f, height_cm: e.target.value })} />
              </div>
              {f.length_cm > 0 && f.width_cm > 0 && f.height_cm > 0 && (
                <p className="mt-1 text-xs text-muted-foreground">CBM : {((f.length_cm * f.width_cm * f.height_cm) / 1000000).toFixed(3)}</p>
              )}
            </div>
            <div className="col-span-2">
              <label className="flex items-center gap-1.5 text-sm font-medium cursor-pointer text-accent">
                <Camera className="h-4 w-4" /> Photos après traitement ({photos.length}/5)
                <input type="file" accept="image/*" multiple className="hidden" onChange={addPhoto} data-testid="wh-lot-photos" />
              </label>
              {photos.length > 0 && (
                <div className="mt-2 flex gap-2 flex-wrap">
                  {photos.map((ph, i) => (
                    <div key={i} className="relative">
                      <img src={ph} alt="" className="h-14 w-14 object-cover border" />
                      <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full p-0.5"><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="col-span-2"><Label>Notes</Label><Textarea data-testid="wh-lot-notes" className="rounded-sm" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button data-testid="wh-lot-submit" className="rounded-sm bg-accent text-white" onClick={submit}>
              {sel?.status === "to_consolidate" ? "CONSOLIDATION TERMINÉE" : "COLIS PRÉPARÉS"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
